import {MergeMethodContextProvider, validateMergeMethod} from '@github-ui/mergebox'
import {MergeMethod} from '@github-ui/mergebox/types'
import {useRef, useState} from 'react'

import type {MergeabilityData} from '../use-mergeability-data'
import {useGoToMergeBox} from '../../../mergebox/use-go-to-mergebox'
import {MergeControl} from '@github-ui/mergebox/components/sections/merge/MergeControl'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {Button} from '@primer/react'
import {usePullRequestAnalytics} from '../../../../../hooks/use-pull-request-analytics'

type OverviewMergeControlProps = {
  data: MergeabilityData | null
  fallbackAction?: 'merge' | 'view-status'
  inactive?: boolean
  itemName: string
}

// we're passing in MergeabilityData because consumers already have it but we can easily read it directly from the hook if needed
// passing it in directly prevents extra overhead and possible live update request duplication
export function OverviewMergeControl({data, fallbackAction = 'merge', inactive, itemName}: OverviewMergeControlProps) {
  const goToMergeBox = useGoToMergeBox()
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()
  const isStacked = data?.stackSection?.isStacked ?? false

  // we're not supporting the overview merge control for stacked pull requests quite yet
  // NOTE - before we can support stack merges we need to add `stackMergeabilityQueries` to the calculator init in the useMergeboxStatusCalculator hook
  if (isFeatureEnabled('pull_request_overview_merge_control') && data && !isStacked) {
    return <OverviewMergeControlWrapper data={data} />
  }

  const isStatusAction = fallbackAction === 'view-status' || isStacked

  return (
    <Button
      size="small"
      variant={isStatusAction ? 'default' : 'primary'}
      inactive={inactive}
      onClick={event => {
        if (inactive) return

        sendPullRequestAnalyticsEvent('overview.for_you_item_action', 'OVERVIEW_FOR_YOU_ITEM', {
          item_name: itemName,
          action_name: isStatusAction ? 'view_merge_status' : 'go_to_merge_box',
        })
        goToMergeBox(event.currentTarget)
      }}
    >
      {isStatusAction ? 'View merge status' : 'Merge when ready'}
    </Button>
  )
}

function OverviewMergeControlWrapper({data}: {data: MergeabilityData}) {
  const [isConfirmingMergeInfo, setIsConfirmingMergeInfo] = useState(false)
  const [shouldFocusPrimaryMergeButton, setShouldFocusPrimaryMergeButton] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  if (!data.mergeRequirements) return null

  const {
    conflictsSection,
    defaultMergeMethod,
    helpUrl,
    mergeBoxPullRequest,
    mergeBoxRollupStatus,
    mergeRequirements,
    mergeSection,
    stackSection,
  } = data

  const mergeMethod = validateMergeMethod(defaultMergeMethod) ? defaultMergeMethod : MergeMethod.MERGE

  return (
    <div ref={containerRef} tabIndex={-1}>
      <MergeMethodContextProvider defaultMergeMethod={mergeMethod}>
        <MergeControl
          baseRefName={mergeSection.baseRefName}
          canUserPushToBase={mergeSection.canUserPushToBase}
          conflictsCondition={conflictsSection.conflictsCondition ?? undefined}
          handleConfirmingMergeInfo={setIsConfirmingMergeInfo}
          helpUrl={helpUrl}
          isConfirmingMergeInfo={isConfirmingMergeInfo}
          isStackMergeInProgress={stackSection.isStackMergeInProgress}
          isStacked={stackSection.isStacked}
          mergeBoxRollupStatus={mergeBoxRollupStatus}
          mergeCurrentlyBlockedForStacks={stackSection.isMergeCurrentlyBlocked}
          mergeRequirements={mergeRequirements}
          onStackMergePendingChange={() => containerRef.current?.focus()}
          pullRequest={mergeBoxPullRequest}
          setShouldFocusPrimaryMergeButton={setShouldFocusPrimaryMergeButton}
          shouldFocusPrimaryMergeButton={shouldFocusPrimaryMergeButton}
          stackedEntries={stackSection.stackEntries ?? []}
          stackedEntriesToMergeCount={stackSection.entriesToMergeCount}
          stackMetadata={stackSection.stackMetadata}
          status={mergeSection.mergeabilityStatus}
        >
          <MergeControl.Control
            buttonSize="small"
            suppressInstructions
            directMergeConfirmation="dialog"
            bypassToggleRendering="menu-option"
          />
        </MergeControl>
      </MergeMethodContextProvider>
    </div>
  )
}
